import { z } from 'zod';

/**
 * The visual PDF-template editor's content.
 *
 * An ordered list of blocks an admin assembles without writing HTML — a
 * heading, a paragraph, a row of fields, the line-items table, the totals
 * block. `compileBlocksToHtml` turns them into exactly the same
 * `{{token}}` / `{{#each lines}}…{{/each}}` HTML a hand-written (`CODE`
 * mode) template body already is, so the PDF pipeline — `mergeTemplate`,
 * `expandSections`, `document-template-render.ts` — never has to know which
 * mode produced a template. Only the editor and this compiler do.
 */

export const documentBlockSchema = z.discriminatedUnion('type', [
  z.object({ id: z.string(), type: z.literal('heading'), text: z.string().max(300) }),
  z.object({ id: z.string(), type: z.literal('paragraph'), text: z.string().max(4000) }),
  z.object({
    id: z.string(),
    type: z.literal('fieldRow'),
    /** Merge tokens shown side by side, each with its own label. */
    tokens: z.array(z.string().max(80)).min(1).max(6),
  }),
  z.object({ id: z.string(), type: z.literal('address'), source: z.enum(['billTo', 'org']) }),
  z.object({ id: z.string(), type: z.literal('lineItems') }),
  z.object({ id: z.string(), type: z.literal('totals') }),
  z.object({ id: z.string(), type: z.literal('spacer') }),
  z.object({ id: z.string(), type: z.literal('divider') }),
]);
export type DocumentBlock = z.infer<typeof documentBlockSchema>;

export const documentBlocksSchema = z.array(documentBlockSchema).max(100);

/** A new, empty visual template — a reasonable starting layout, all editable. */
export function defaultBlocks(): DocumentBlock[] {
  return [
    { id: 'b1', type: 'heading', text: '{{org.name}}' },
    { id: 'b2', type: 'address', source: 'org' },
    { id: 'b3', type: 'spacer' },
    { id: 'b4', type: 'fieldRow', tokens: ['document.number', 'document.date', 'document.dueDate'] },
    { id: 'b5', type: 'heading', text: 'Bill to' },
    { id: 'b6', type: 'address', source: 'billTo' },
    { id: 'b7', type: 'spacer' },
    { id: 'b8', type: 'lineItems' },
    { id: 'b9', type: 'totals' },
    { id: 'b10', type: 'divider' },
    { id: 'b11', type: 'paragraph', text: '{{document.terms}}' },
  ];
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/gu, '&amp;')
    .replace(/</gu, '&lt;')
    .replace(/>/gu, '&gt;')
    .replace(/"/gu, '&quot;');
}

/**
 * A block's free-text may itself contain `{{token}}` placeholders. They are
 * left untouched here — `mergeTemplate` substitutes them later, in the same
 * pass a `CODE` template's body goes through — everything else in the text
 * is escaped so a literal `<` an admin types cannot become a tag.
 */
function textToHtml(text: string): string {
  const parts = text.split(/(\{\{\s*[\w.]+\s*\}\})/gu);
  return parts
    .map((part) => (/^\{\{\s*[\w.]+\s*\}\}$/u.test(part) ? part : escapeHtml(part)))
    .join('')
    .replace(/\n/gu, '<br/>');
}

const FIELD_ROW_LABELS: Record<string, string> = {
  'document.number': 'Number',
  'document.date': 'Date',
  'document.dueDate': 'Due',
  'document.status': 'Status',
  'document.currency': 'Currency',
  'document.subtotal': 'Subtotal',
  'document.total': 'Total',
};

function labelFor(token: string): string {
  return (
    FIELD_ROW_LABELS[token] ??
    token
      .split('.')
      .pop()!
      .replace(/([a-z])([A-Z])/gu, '$1 $2')
      .replace(/^./u, (char) => char.toUpperCase())
  );
}

/** Compiles the visual editor's blocks into a `DocumentTemplate.body`. */
export function compileBlocksToHtml(blocks: readonly DocumentBlock[]): string {
  return blocks.map(renderBlock).join('\n');
}

function renderBlock(block: DocumentBlock): string {
  switch (block.type) {
    case 'heading':
      return `<h2 class="wt-heading">${textToHtml(block.text)}</h2>`;
    case 'paragraph':
      return `<p class="wt-paragraph">${textToHtml(block.text)}</p>`;
    case 'fieldRow':
      return `<div class="wt-field-row">${block.tokens
        .map(
          (token) =>
            `<div class="wt-field"><span class="wt-field-label">${escapeHtml(labelFor(token))}</span><span class="wt-field-value">{{${token}}}</span></div>`,
        )
        .join('')}</div>`;
    case 'address':
      return `<div class="wt-address">${block.source === 'org' ? '{{org.address}}' : '{{billTo.address}}'}</div>`;
    case 'lineItems':
      return [
        '<table class="wt-lines"><thead><tr>',
        '<th>Description</th><th>Qty</th><th>Unit price</th><th>Discount</th><th>Tax</th><th>Total</th>',
        '</tr></thead><tbody>',
        '{{#each lines}}',
        '<tr><td>{{description}}</td><td>{{quantity}}</td><td>{{unitPrice}}</td><td>{{discountPercent}}%</td><td>{{taxRate}}%</td><td>{{lineTotal}}</td></tr>',
        '{{/each}}',
        '</tbody></table>',
      ].join('');
    case 'totals':
      return [
        '<div class="wt-totals">',
        '<div><span>Subtotal</span><span>{{document.subtotal}}</span></div>',
        '<div><span>Discount</span><span>{{document.discount}}</span></div>',
        '<div><span>Tax</span><span>{{document.tax}}</span></div>',
        '<div class="wt-totals-grand"><span>Total</span><span>{{document.total}}</span></div>',
        '</div>',
      ].join('');
    case 'spacer':
      return '<div class="wt-spacer"></div>';
    case 'divider':
      return '<hr class="wt-divider"/>';
  }
}

/** Base CSS for the elements `compileBlocksToHtml` produces. Appended after any custom `styles`. */
export const VISUAL_TEMPLATE_BASE_STYLES = `
.wt-heading { margin: 0 0 8px; font-size: 18px; }
.wt-paragraph { margin: 0 0 8px; white-space: pre-wrap; }
.wt-field-row { display: flex; gap: 24px; margin: 8px 0; flex-wrap: wrap; }
.wt-field { display: flex; flex-direction: column; }
.wt-field-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.04em; opacity: 0.65; }
.wt-address { white-space: pre-line; margin: 4px 0; }
.wt-spacer { height: 16px; }
.wt-divider { border: none; border-top: 1px solid #ddd; margin: 16px 0; }
.wt-lines { width: 100%; border-collapse: collapse; margin: 12px 0; }
.wt-lines th, .wt-lines td { text-align: left; padding: 6px 8px; border-bottom: 1px solid #eee; font-size: 12px; }
.wt-totals { margin-left: auto; width: 260px; }
.wt-totals div { display: flex; justify-content: space-between; padding: 3px 0; font-size: 12px; }
.wt-totals-grand { font-weight: 700; border-top: 1px solid #ccc; margin-top: 4px; padding-top: 6px !important; }
`;
